/**
 * Rosie — NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { format, parse, subMinutes } from 'date-fns';
import { todayET, etToISO } from '../utils/et';

const FACILITIES = [
  'Lancaster General Hospital (LGH)',
  'Lancaster Behavioral Health Hospital (LBHH)',
  'Lancaster Rehabilitation Hospital (LRH)',
  'Willow Valley',
  'Private / Other',
];

const FACILITY_ADDRESSES = {
  'Lancaster General Hospital (LGH)': '555 N Duke St, Lancaster, PA 17602',
  'Lancaster Behavioral Health Hospital (LBHH)': '333 Harrisburg Ave, Lancaster, PA 17603',
  'Lancaster Rehabilitation Hospital (LRH)': '675 Good Dr, Lancaster, PA 17601',
  'Willow Valley': '675 Willow Valley Lakes Dr, Willow Street, PA 17584',
};

const AVERAGE_SPEEDS = {
  local: 25,
  highway: 55,
};

function AddressField({ label, value, onChange, onPlaceSelect, placeholder }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const timerRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (val) => {
    onChange(val);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (val.length < 3) { setSuggestions([]); setShowDropdown(false); return; }
    timerRef.current = setTimeout(async () => {
      try {
        const res = await api.get('/estimate/autocomplete?q=' + encodeURIComponent(val));
        setSuggestions(res.data || []);
        setShowDropdown((res.data || []).length > 0);
      } catch (e) { setSuggestions([]); }
    }, 250);
  };

  const handleSelect = (label) => {
    onPlaceSelect(label);
    setSuggestions([]);
    setShowDropdown(false);
  };

  return (
    <div style={{ position: 'relative' }} ref={wrapperRef}>
      <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>{label}</label>
      <input value={value} onChange={e => handleChange(e.target.value)}
        onFocus={() => { if (suggestions.length > 0) setShowDropdown(true); }}
        placeholder={placeholder} style={{ width: '100%' }} />
      {showDropdown && suggestions.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white',
          border: '1px solid #ddd', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          zIndex: 1000, maxHeight: 200, overflowY: 'auto' }}>
          {suggestions.map((s, i) => (
            <div key={i} onClick={() => handleSelect(s.label)}
              style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', borderBottom: i < suggestions.length - 1 ? '1px solid #f0f0f0' : 'none' }}
              onMouseEnter={e => e.target.style.background = '#f5f5f3'}
              onMouseLeave={e => e.target.style.background = 'white'}>
              {s.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function estimatePickupTime(appointmentTime, originAddress, destAddress) {
  if (!appointmentTime) return '';
  try {
    const apptDate = parse(appointmentTime, 'HH:mm', new Date());
    const bufferMinutes = 20;
    const driveEstimate = 45;
    const pickupDate = subMinutes(apptDate, driveEstimate + bufferMinutes);
    return format(pickupDate, 'HH:mm');
  } catch {
    return '';
  }
}

export default function CreateTripModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    tripDate: todayET(),
    pickupTime: '',
    askedTime: '',
    appointmentTime: '',
    requestingFacility: '',
    patientFirstName: '',
    patientLastName: '',
    patientIdentifier: '',
    identifierType: 'mrn',
    originAddress: '',
    originAddressDetails: '',
    destinationAddress: '',
    destinationAddressDetails: '',
    requiresWheelchair: true,
    patientType: 'wheelchair',
    notes: '',
    roundTrip: false,
    returnTime: '',
    returnIsWillCall: false,
    driverWaits: false,
    timeUnknown: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Live private-pay quote (Paula 2026-10-05). Dispatch quotes the caller
  // while still on the phone, before the trip exists, so the figure has to be
  // on the form, not on the saved trip. Whatever is in "price quoted" is sent
  // as quotedTotal and locked as the trip's prepay amount.
  const [liveQuote, setLiveQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quotedPrice, setQuotedPrice] = useState('');
  const quoteTimer = useRef(null);
  const isPrivate = form.requestingFacility === 'Private / Other';
  useEffect(() => {
    if (!isPrivate || !form.originAddress || !form.destinationAddress || !form.tripDate || !form.pickupTime) { setLiveQuote(null); return; }
    if (quoteTimer.current) clearTimeout(quoteTimer.current);
    quoteTimer.current = setTimeout(async () => {
      setQuoteLoading(true);
      try {
        const when = etToISO(`${form.tripDate}T${form.pickupTime}`);
        const res = await api.post('/estimate', {
          originAddress: form.originAddressDetails ? `${form.originAddress}, ${form.originAddressDetails}` : form.originAddress,
          destinationAddress: form.destinationAddressDetails ? `${form.destinationAddress}, ${form.destinationAddressDetails}` : form.destinationAddress,
          vehicleType: form.requiresWheelchair ? 'wheelchair_van' : 'sedan',
          roundTrip: !!form.roundTrip,
          tripDate: when, departureTime: when,
          driverStays: !!(form.roundTrip && form.driverWaits),
          timeUnknown: !!form.timeUnknown,
        });
        const q = res.data;
        setLiveQuote(q);
        // Prefill the spoken price with Rosie's figure unless dispatch already typed one.
        setQuotedPrice(prev => (prev === '' || prev === String(liveQuote && liveQuote.grandTotal)) ? String(q.grandTotal) : prev);
      } catch (e) { setLiveQuote({ error: true }); }
      finally { setQuoteLoading(false); }
    }, 600);
    return () => { if (quoteTimer.current) clearTimeout(quoteTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrivate, form.originAddress, form.originAddressDetails, form.destinationAddress, form.destinationAddressDetails, form.tripDate, form.pickupTime, form.requiresWheelchair, form.roundTrip, form.driverWaits, form.timeUnknown]);

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target ? e.target.value : e }));

  const handleFacilityChange = (facility) => {
    const address = FACILITY_ADDRESSES[facility] || '';
    setForm(f => ({ ...f, requestingFacility: facility, originAddress: address }));
  };

  const handleAppointmentTime = (time) => {
    const suggested = estimatePickupTime(time, form.originAddress, form.destinationAddress);
    setForm(f => ({ ...f, appointmentTime: time, pickupTime: f.pickupTime || suggested }));
  };

  const handleSave = async () => {
    if (!form.tripDate || !form.pickupTime || !form.originAddress || !form.destinationAddress) {
      setError('Date, pickup time, pickup address and destination are required');
      return;
    }
    // A return leg must have EITHER a pickup time OR the will-call flag
    // (Paula 2026-09-18). Neither set produced TRP-2026-3629: a return leg with
    // no time and no will-call, which sat open on the board for days, never
    // showed on the will-call list, and could not be dispatched. One of the two
    // is always true, so neither being set is never right.
    if (form.roundTrip && !form.returnIsWillCall && !form.returnTime) {
      setError('This return leg has no pickup time. Enter one, or switch on "Will call" if the patient will phone when they are ready.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const scheduledTime = etToISO(`${form.tripDate}T${form.pickupTime}`);
      const originFull = form.originAddressDetails
        ? `${form.originAddress}, ${form.originAddressDetails}`
        : form.originAddress;
      const destFull = form.destinationAddressDetails
        ? `${form.destinationAddress}, ${form.destinationAddressDetails}`
        : form.destinationAddress;
      const patientName = [form.patientLastName, form.patientFirstName].filter(Boolean).join(', ');
      const notesParts = [
        patientName ? `Patient: ${patientName}` : '',
        form.patientIdentifier ? `${form.identifierType.toUpperCase()}: ${form.patientIdentifier}` : '',
        form.appointmentTime ? `Appt: ${form.appointmentTime}` : '',
        form.notes,
      ].filter(Boolean);

      // If round trip + specific return time (not will-call), convert it to an ISO
      // using the same trip date as the outbound leg.
      let returnTimeIso = null;
      if (form.roundTrip && !form.returnIsWillCall && form.returnTime) {
        returnTimeIso = etToISO(`${form.tripDate}T${form.returnTime}`);
      }

      // Pushed in the LGH group text? Keep what they first asked for so the
      // push is measurable (capacity tracking, 2026-09-25).
      const requestedTime = form.askedTime ? etToISO(`${form.tripDate}T${form.askedTime}`) : scheduledTime;
      await api.post('/transports', {
        requestedTime,
        scheduledTime,
        originAddress: originFull,
        destinationAddress: destFull,
        requiresWheelchair: form.requiresWheelchair,
        patientType: form.patientType,
        vehicleTypeUsed: form.requiresWheelchair ? 'wheelchair_van' : 'sedan',
        requestChannel: 'manual',
        requestRawText: form.requestingFacility || null,
        // Private payer -> the server computes and stores a quote, so the
        // customer can actually pay at /pay. Without it the trip is invisible
        // to the pay page (TRP-2026-3628).
        billToPatient: form.requestingFacility === 'Private / Other',
        quotedTotal: (form.requestingFacility === 'Private / Other' && quotedPrice !== '' && Number(quotedPrice) > 0) ? Number(quotedPrice) : undefined,
        timeUnknown: form.requestingFacility === 'Private / Other' ? !!form.timeUnknown : undefined,
        quotedTotalOvernight: (form.requestingFacility === 'Private / Other' && form.timeUnknown && liveQuote && liveQuote.overnightAlternative) ? Number(liveQuote.overnightAlternative.grandTotal) : undefined,
        // Driver waits at the destination instead of returning to base, so the
        // quote is one continuous clock with mileage counted once.
        driverWaits: form.roundTrip && form.driverWaits,
        notes: notesParts.join(' | ') || null,
        numPassengers: 1,
        roundTrip: form.roundTrip,
        returnTime: returnTimeIso,
        returnIsWillCall: form.roundTrip && form.returnIsWillCall,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError('Could not create trip — ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '16px' }}>
      <div style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 480, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ padding: '16px 20px', borderBottom: '0.5px solid #e0e0db', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: 16, fontWeight: 500, margin: 0 }}>New trip</h2>
          <button onClick={onClose} style={{ fontSize: 13, padding: '4px 10px' }}>Cancel</button>
        </div>

        <div style={{ padding: '16px 20px' }}>
          {error && (
            <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
              {error}
            </div>
          )}

          <div style={{ fontSize: 11, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Schedule</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Date</label>
              <input type="date" value={form.tripDate} onChange={set('tripDate')} style={{ width: '100%' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Appointment time</label>
              <input type="time" value={form.appointmentTime} onChange={e => handleAppointmentTime(e.target.value)} style={{ width: '100%' }} />
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>
              Pickup time
              {form.appointmentTime && !form.pickupTime && (
                <span style={{ color: '#185FA5', marginLeft: 6, fontSize: 11 }}>auto-calculated from appointment</span>
              )}
            </label>
            <input type="time" value={form.pickupTime} onChange={set('pickupTime')} style={{ width: '100%' }} />
          </div>

          <div style={{ marginBottom: 12, background: '#FFF7E6', border: '0.5px solid #FAC775', borderRadius: 8, padding: '8px 10px' }}>
            <label style={{ fontSize: 12, color: '#854F0B', display: 'block', marginBottom: 4 }}>
              Pushed? Time they first asked for <span style={{ color: '#999' }}>(optional)</span>
            </label>
            <input type="time" value={form.askedTime} onChange={set('askedTime')} style={{ width: '100%' }} />
            {form.askedTime && form.pickupTime && form.askedTime !== form.pickupTime && (
              <div style={{ fontSize: 11, color: '#854F0B', marginTop: 4 }}>Recorded as a push from {form.askedTime} to {form.pickupTime}.</div>
            )}
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Requesting facility</label>
            <select value={form.requestingFacility} onChange={e => handleFacilityChange(e.target.value)} style={{ width: '100%' }}>
              <option value="">Select facility...</option>
              {FACILITIES.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>

          <div style={{ fontSize: 11, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '16px 0 10px' }}>Patient</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>First name</label>
              <input value={form.patientFirstName} onChange={set('patientFirstName')} style={{ width: '100%' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Last name</label>
              <input value={form.patientLastName} onChange={set('patientLastName')} style={{ width: '100%' }} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>ID type</label>
              <select value={form.identifierType} onChange={set('identifierType')} style={{ width: '100%' }}>
                <option value="mrn">MRN</option>
                <option value="dob">DOB</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>{form.identifierType === 'mrn' ? 'MRN' : 'Date of birth'}</label>
              <input
                value={form.patientIdentifier}
                onChange={set('patientIdentifier')}
                type={form.identifierType === 'dob' ? 'date' : 'text'}
                placeholder={form.identifierType === 'mrn' ? 'e.g. 12345' : ''}
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Patient type</label>
            <select value={form.patientType} onChange={set('patientType')} style={{ width: '100%' }}>
              <option value="wheelchair">Wheelchair</option>
              <option value="ambulatory">Ambulatory</option>
              <option value="bh">BH</option>
              <option value="201">BH 201</option>
              <option value="302">BH 302</option>
              <option value="304">BH 304</option>
              <option value="305">BH 305</option>
              <option value="da">D&A</option>
            </select>
          </div>

          <div style={{ fontSize: 11, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '16px 0 10px' }}>Route</div>

          <div style={{ marginBottom: 6 }}>
            <AddressField
              label="Pickup address"
              value={form.originAddress}
              onChange={v => setForm(f => ({...f, originAddress: v}))}
              onPlaceSelect={v => setForm(f => ({...f, originAddress: v}))}
              placeholder="Start typing an address..."
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Room / floor / apt (optional)</label>
            <input value={form.originAddressDetails} onChange={set('originAddressDetails')} placeholder="e.g. Room 412B, 3rd floor" style={{ width: '100%' }} />
          </div>

          <div style={{ marginBottom: 6 }}>
            <AddressField
              label="Destination address"
              value={form.destinationAddress}
              onChange={v => setForm(f => ({...f, destinationAddress: v}))}
              onPlaceSelect={v => setForm(f => ({...f, destinationAddress: v}))}
              placeholder="Start typing an address..."
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Room / floor / apt (optional)</label>
            <input value={form.destinationAddressDetails} onChange={set('destinationAddressDetails')} placeholder="e.g. Suite 200" style={{ width: '100%' }} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div
              onClick={() => setForm(f => ({ ...f, requiresWheelchair: !f.requiresWheelchair }))}
              style={{ width: 36, height: 20, borderRadius: 10, cursor: 'pointer', background: form.requiresWheelchair ? '#185FA5' : '#ddd', position: 'relative', transition: 'background 0.15s', flexShrink: 0 }}
            >
              <div style={{ width: 16, height: 16, borderRadius: '50%', background: 'white', position: 'absolute', top: 2, left: form.requiresWheelchair ? 18 : 2, transition: 'left 0.15s' }} />
            </div>
            <span style={{ fontSize: 13 }}>Requires wheelchair van</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: form.roundTrip ? 10 : 12 }}>
            <div
              onClick={() => setForm(f => ({ ...f, roundTrip: !f.roundTrip }))}
              style={{ width: 36, height: 20, borderRadius: 10, cursor: 'pointer', background: form.roundTrip ? '#185FA5' : '#ddd', position: 'relative', transition: 'background 0.15s', flexShrink: 0 }}
            >
              <div style={{ width: 16, height: 16, borderRadius: '50%', background: 'white', position: 'absolute', top: 2, left: form.roundTrip ? 18 : 2, transition: 'left 0.15s' }} />
            </div>
            <span style={{ fontSize: 13 }}>Make this a round trip</span>
          </div>

          {isPrivate && (
            <div style={{ background: '#FFF7E6', border: '0.5px solid #F0C36D', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 12, color: '#854F0B' }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Private payer</div>
              {!form.originAddress || !form.destinationAddress || !form.pickupTime ? (
                <div>Enter pickup, destination and time and Rosie prices the trip here while you are on the phone.</div>
              ) : quoteLoading ? (
                <div>Pricing...</div>
              ) : liveQuote && !liveQuote.error ? (
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#333' }}>Rosie estimate: ${Number(liveQuote.grandTotal).toFixed(2)}{form.roundTrip ? ' round trip' : ''}</div>
                  <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>
                    {(liveQuote.leg1 && liveQuote.leg1.breakdown ? liveQuote.leg1.breakdown : []).join(' | ')}
                    {liveQuote.tollEstimate > 0 ? ` | Tolls est. $${Number(liveQuote.tollEstimate).toFixed(2)}` : ''}
                  </div>
                </div>
              ) : (
                <div>Rosie could not price this route. Type the price you quote below and it will still be locked.</div>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#333', marginTop: 8 }}>
                <input type="checkbox" checked={!!form.timeUnknown} onChange={e => setForm(f => ({ ...f, timeUnknown: e.target.checked }))} />
                Pickup time not known yet (surgery / procedure, hospital calls later). Quote both prices.
              </label>
              {form.timeUnknown && liveQuote && liveQuote.overnightAlternative && (
                <div style={{ marginTop: 6, fontSize: 13, color: '#333' }}>
                  Tell the caller: <strong>${Number(liveQuote.grandTotal).toFixed(2)} daytime</strong>, or <strong>${Number(liveQuote.overnightAlternative.grandTotal).toFixed(2)}</strong> if the pickup is between 11 PM and 6:59 AM (two transporters). Both lock on the trip; Rosie switches when the real time comes in.
                </div>
              )}
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#333' }}>Price quoted to the caller{form.timeUnknown ? ' (daytime)' : ''} $</label>
                <input type="number" inputMode="decimal" step="0.01" min="0" value={quotedPrice} onChange={e => setQuotedPrice(e.target.value)}
                  style={{ width: 110, fontSize: 15, fontWeight: 600, padding: '6px 8px' }} />
                {liveQuote && !liveQuote.error && quotedPrice !== '' && Math.abs(Number(quotedPrice) - Number(liveQuote.grandTotal)) > 0.5 && (
                  <span style={{ fontSize: 11, color: '#A32D2D' }}>differs from Rosie's ${Number(liveQuote.grandTotal).toFixed(2)}</span>
                )}
              </div>
              <div style={{ fontSize: 11, marginTop: 6 }}>This is the number the customer pays. It is locked on the trip when you save and is what the pay page and the invoice use.</div>
            </div>
          )}

          {form.roundTrip && (
            <div style={{ background: '#f9f9f8', border: '0.5px solid #e0e0db', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: '#666', marginBottom: 8 }}>
                A second trip will be created in the opposite direction (destination → pickup).
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div
                  onClick={() => setForm(f => ({ ...f, driverWaits: !f.driverWaits }))}
                  style={{ width: 32, height: 18, borderRadius: 9, cursor: 'pointer', background: form.driverWaits ? '#1D9E75' : '#ddd', position: 'relative', transition: 'background 0.15s', flexShrink: 0 }}
                >
                  <div style={{ width: 14, height: 14, borderRadius: '50%', background: 'white', position: 'absolute', top: 2, left: form.driverWaits ? 16 : 2, transition: 'left 0.15s' }} />
                </div>
                <span style={{ fontSize: 12 }}>Driver waits at the appointment (one continuous trip, not two)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div
                  onClick={() => setForm(f => ({ ...f, returnIsWillCall: !f.returnIsWillCall }))}
                  style={{ width: 32, height: 18, borderRadius: 9, cursor: 'pointer', background: form.returnIsWillCall ? '#BA7517' : '#ddd', position: 'relative', transition: 'background 0.15s', flexShrink: 0 }}
                >
                  <div style={{ width: 14, height: 14, borderRadius: '50%', background: 'white', position: 'absolute', top: 2, left: form.returnIsWillCall ? 16 : 2, transition: 'left 0.15s' }} />
                </div>
                <span style={{ fontSize: 12 }}>Will call — patient will call when ready for pickup</span>
              </div>
              {!form.returnIsWillCall && (
                <div>
                  <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Return pickup time</label>
                  <input
                    type="time"
                    value={form.returnTime}
                    onChange={set('returnTime')}
                    style={{ width: '100%' }}
                  />
                </div>
              )}
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Notes (optional)</label>
            <textarea value={form.notes} onChange={set('notes')} rows={2} style={{ width: '100%', resize: 'none' }} />
          </div>
        </div>

        <div style={{ padding: '12px 20px', borderTop: '0.5px solid #e0e0db', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ fontSize: 13 }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ fontSize: 13, background: '#185FA5', color: 'white', border: '0.5px solid #185FA5', padding: '6px 16px' }}>
            {saving ? 'Saving...' : 'Create trip'}
          </button>
        </div>
      </div>
    </div>
  );
}